import * as api from "../api/index";
import {
  START_LOADING,
  END_LOADING,
  FETCH_ITEMS,
  FETCH_ITEMS_USER,
  ADD_NEW_ITEM,
  ADD_NEW_ITEMS,
  UPDATE_ITEM,
  DELETE_ITEM,
  DELETE_ITEMS_BY_CATEGORY,
  ADD_NEW_STOCK,
  ADD_NEW_STOCKS,
  CREATE_FUTURE_PRICE_LIST,
  FETCH_FUTURE_PRICE_LIST,
  UPDATE_FUTURE_PRICE,
  DELETE_FUTURE_PRICE,
} from "./constants";

// Was one request for the entire catalog - on PosMainScreen/ItemsList, every
// login or fresh visit waited on that whole payload before showing a single
// product. Now the first page dispatches (and unblocks the UI) as soon as it
// arrives; remaining pages stream in behind it and just extend the same
// items array, so nothing downstream (ItemCards, ItemsList, search, filters)
// needs to know loading is still happening in the background - see
// getItems in server/controllers/items.js for the paginated response shape.
const ITEMS_PAGE_SIZE = 60;

// Each streamed page used to be merged into a *locally tracked* `accumulated`
// snapshot and dispatched as a full replacement of state.items - so an item
// created (ADD_NEW_ITEM/ADD_NEW_ITEMS) while a later page was still in
// flight got silently wiped the moment that page landed, since the replace
// payload was built from data captured before the new item ever existed.
// That's exactly what made a just-added item vanish from ItemsList/POS until
// a full page reload. Merging into whatever is *currently* in the store
// (by _id, upserting) instead of blindly replacing means a concurrent add
// survives every later page - each page can only add/update rows, never
// discard ones already there.
export const getAllItems = () => async (dispatch, getState) => {
  const mergePage = (pageItems) => {
    const byId = new Map(getState().items.items.map((it) => [it._id, it]));
    pageItems.forEach((it) => byId.set(it._id, it));
    return Array.from(byId.values());
  };
  try {
    dispatch({ type: START_LOADING });
    const { data: firstPage } = await api.fetchItems({ page: 1, limit: ITEMS_PAGE_SIZE });
    dispatch({ type: FETCH_ITEMS, payload: mergePage(firstPage.items) });
    dispatch({ type: END_LOADING });

    let page = 2;
    let hasMore = firstPage.hasMore;
    while (hasMore) {
      const { data: nextPage } = await api.fetchItems({ page, limit: ITEMS_PAGE_SIZE });
      dispatch({ type: FETCH_ITEMS, payload: mergePage(nextPage.items) });
      hasMore = nextPage.hasMore;
      page += 1;
    }
  } catch (error) {
    console.log(error);
    dispatch({ type: END_LOADING });
  }
};
export const getItemsByUser = (searchQuery) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    const {
      data: { data },
    } = await api.fetchItemsByUser(searchQuery);
    dispatch({ type: FETCH_ITEMS_USER, payload: data });
  } catch (error) {
    console.log("getItemsByuser in itemActions error");
    console.log(error.response);
  } finally {
    dispatch({ type: END_LOADING });
  }
};

// export const createItem = (item, openSnackbar) => async (dispatch) => {
//   try {
//     const { data } = await api.addItem(item);
//     dispatch({ type: ADD_NEW_ITEM, payload: data });

//     // Check if stock.quantity and stock.unit are defined
//     if (data.units?.stock?.quantity && data.units?.stock?.unit) {
//       const { initialStockdata } = await api.addStock([
//         {
//           item: data._id,
//           transactionType: "MASTER",
//           qty: data.units.stock.quantity,
//           unit: data.units.stock.unit,
//           categoryId: data.category.categoryId,
//           createdAt: data.createdAt,
//           references: {
//             transactionRef: "Stock",
//           },
//           creator: data.creator || "65549a5b77d1c64130b5db0c",
//         },
//       ]);
//       dispatch({ type: ADD_NEW_STOCK, payload: initialStockdata });
//     }

//     openSnackbar("Item added successfully");
//   } catch (error) {
//     console.log(error);
//   }
// };

// actions/itemActions.js
export const createItem = (item, openSnackbar) => async (dispatch) => {
  try {
    console.log("Item data sent to api.addItem:", item);
    const { data } = await api.addItem(item);
    console.log("Response from api.addItem:", data);
    dispatch({ type: ADD_NEW_ITEM, payload: data });
    openSnackbar(`Item "${data.itemName}" added successfully`);
    // Opening stock is written as a MASTER ledger row inside the server
    // createItem handler. A second client POST used to fail independently
    // and leave available stock at 0 while opening stock still showed 1.
    return data;
  } catch (error) {
    console.error("Error in createItem:", error.message, error.stack);
    openSnackbar("Failed to add item: " + error.message);
    throw error;
  }
};

export const addStockForItem = (item, stockData, openSnackbar) => async (dispatch) => {
  try {
    const stockTransaction = {
      item: item._id,
      transactionType: "MASTER",
      qty: parseFloat(stockData.quantity),
      unit: stockData.unit?._id || stockData.unit,
      category: item.category?.categoryId,
      createdAt: item.createdAt,
      references: { transactionRef: "Stock" },
    };
    console.log("Stock data to be sent to api.addStock:", stockTransaction);

    const response = await api.addStock([stockTransaction]);
    const stockDataResponse = response.data;
    console.log("Response from api.addStock:", stockDataResponse);

    const payload = Array.isArray(stockDataResponse) ? stockDataResponse : [stockDataResponse];
    dispatch({ type: ADD_NEW_STOCK, payload });
    openSnackbar("Stock transaction added successfully");
  } catch (error) {
    console.error("Error in addStockForItem:", error.message, error.stack);
    openSnackbar("Failed to add stock transaction: " + error.message);
    throw error;
  }
};

// Bulk-upload stock transactions (opening stock for new items, corrections for existing
// ones) are recorded via the dedicated recordStockTransactions action below, called
// explicitly from BulkItemUpload.js as its own step - not bundled into item CRUD here.
export const createItems = (items, openSnackbar) => async (dispatch) => {
  const { data } = await api.addItems(items);
  dispatch({ type: ADD_NEW_ITEMS, payload: data });
  openSnackbar("Items added successfully");
  return data;
};
// export const createItems = (item, openSnackbar) => async (dispatch) => {
//   try {
//     const { data } = await api.addItems(item);
//     dispatch({ type: ADD_NEW_ITEM, payload: data });

//     const { initialStockdata } = await api.addStock([
//       {
//         itemName: data.itemName,
//         transactionRef: "Item Master",
//         qty: data.stockQty.initialQty,
//         unitPrice: data.unitPrice,
//         categoryId: data.category.categoryId,
//         itemId: data._id,
//         createdAt: data.createdAt,
//       },
//     ]);
//     dispatch({ type: ADD_NEW_STOCK, payload: initialStockdata });

//     openSnackbar("Item added successfully");
//   } catch (error) {
//     console.log(error);
//   }
// };
export const updateItem = (id, item, openSnackbar) => async (dispatch) => {
  try {
    console.log("Inside updateItems Function in react", item);

    const { data } = await api.updateItem(id, item);
    dispatch({ type: UPDATE_ITEM, payload: data });
    openSnackbar(`Item "${data.itemName}" updated successfully`);
  } catch (error) {
    console.log(error);
  }
};
// export const updateItems = (items, openSnackbar) => async () => {
//   try {
//     console.log("Inside updateItems Function in react", items);
//     ;
//     const { message } = await api.updateItems(items);

//     openSnackbar(message);
//   } catch (error) {
//     console.log(error);
//   }
// };

export const updateItems = (items, openSnackbar) => async (dispatch) => {
  try {
    console.log("Inside updateItems Function in react", items);
    const response = await api.updateItems(items);
    if (!response || !response.data) {
      throw new Error("No valid response from server");
    }
    const { data } = response;
    const updatedDocs = data.updatedDocs || items;
    updatedDocs.forEach((item) => {
      dispatch({ type: UPDATE_ITEM, payload: item });
    });
    openSnackbar(data.message || "Items updated successfully");
    return updatedDocs;
  } catch (error) {
    console.error("Error in updateItems:", error);
    openSnackbar("Failed to update items: " + (error.message || "Unknown error"));
    throw error;
  }
};

// Fast path for bulk-upload updates only (see bulkUpdateItems in
// server/controllers/items.js for why). Doesn't dispatch per-item
// UPDATE_ITEM like updateItems does, since the caller (BulkItemUpload)
// already does a full getAllItems() refresh right after - so there's no
// need to round-trip populated docs back just to discard them.
export const bulkUpdateItems = (items, openSnackbar) => async () => {
  try {
    const { data } = await api.bulkUpdateItems(items);
    if (openSnackbar) {
      openSnackbar(data.message || "Items updated successfully");
    }
    return data;
  } catch (error) {
    console.error("Error in bulkUpdateItems:", error);
    if (openSnackbar) {
      openSnackbar("Failed to update items: " + (error.message || "Unknown error"));
    }
    throw error;
  }
};

// Explicit, standalone step for writing StockTransaction records - used by bulk upload
// so the write is its own visible network call with its own error handling, instead of
// being silently bundled inside item create/update actions where a failure is easy to
// miss. Throws on failure (does not swallow) so the caller can react/report clearly.
export const recordStockTransactions = (transactions, openSnackbar) => async (dispatch) => {
  if (!transactions || transactions.length === 0) return [];
  try {
    console.log("Recording stock transactions:", transactions);
    const { data } = await api.addStock(transactions);
    // insertMany with ordered:false (see server/controllers/stock.js) can
    // partially succeed: a 207 response means some transactions were
    // rejected (bad unit/category reference, etc.) while the rest were
    // still recorded - surface that instead of silently reporting success.
    const insertedDocs = Array.isArray(data) ? data : data.insertedDocs || [];
    if (data.failedCount) {
      openSnackbar(data.message);
    }
    dispatch({ type: ADD_NEW_STOCKS, payload: insertedDocs });
    return insertedDocs;
  } catch (error) {
    console.error("Error recording stock transactions:", error);
    openSnackbar(
      "Failed to record stock transactions: " +
        (error.response?.data?.message || error.message)
    );
    throw error;
  }
};
export const deleteItem = (id, openSnackbar) => async (dispatch) => {
  try {
    await api.deleteItem(id);
    dispatch({ type: DELETE_ITEM, payload: id });
    openSnackbar("Item deleted successfully");
  } catch (error) {
    console.log(error);
  }
};
export const deleteItemsByCategoryId = (id, openSnackbar) => async (dispatch) => {
  
  try {
    await api.deleteItemsByCategoryId(id);
    dispatch({ type: DELETE_ITEMS_BY_CATEGORY, payload: id });
    openSnackbar("Items deleted successfully with category");
  } catch (error) {
    console.log(error);
  }
};

// export const getItemById = (id) => async (dispatch) => { // For specific item lookup
//   try {
//     const { data } = await api.fetchItemById(id);
//     return data;
//   } catch (error) {
//     console.log(error);
//     return null;
//   }
// };


export const toggleItemStatus = (id) => async (dispatch) => {
  try {
    const { data } = await api.toggleItemStatus(id);
    dispatch({ type: UPDATE_ITEM, payload: data });
    dispatch(getAllItems());

    return { success: true };
  } catch (error) {
    console.log(error);
    return { success: false, message: error.message };
  }
};

export const createFuturePriceList = (futurePriceList, openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    const { data } = await api.createFuturePriceList(futurePriceList);
    dispatch({ type: CREATE_FUTURE_PRICE_LIST, payload: data });
    dispatch({ type: END_LOADING });

    const hasUpdates = data.some((result) => result.updated);
    const message = hasUpdates
      ? "Future price list scheduled successfully (some prices updated)"
      : "Future price list scheduled successfully";
    openSnackbar(message);
  } catch (error) {
    console.error("Error creating future price list:", error);
    openSnackbar("Failed to create future price list: " + error.message);
    throw error;
  }
};

export const fetchFuturePriceList = () => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    const { data } = await api.fetchFuturePriceList();
    dispatch({ type: FETCH_FUTURE_PRICE_LIST, payload: data });
    dispatch({ type: END_LOADING });
  } catch (error) {
    console.error("Error fetching future price list:", error);
    throw error;
  }
};

// export const updateFuturePrice = (id, priceData, openSnackbar) => async (dispatch) => {
//   try {
//     dispatch({ type: START_LOADING });
//     const { data } = await api.updateFuturePrice(id, priceData);
//     dispatch({ type: UPDATE_FUTURE_PRICE, payload: data });
//     dispatch({ type: END_LOADING });
//     openSnackbar("Future price updated successfully");
//   } catch (error) {
//     console.error("Error updating future price:", error);
//     openSnackbar("Failed to update future price: " + error.message);
//     throw error;
//   }
// };

export const updateFuturePrice = (id, priceData, openSnackbar) => async (dispatch) => {
  try {
    console.log("Updating future price with payload:", { id, priceData });
    dispatch({ type: "START_LOADING" });
    const { data } = await api.updateFuturePrice(id, priceData);
    console.log("Update future price response:", data);
    dispatch({ type: "UPDATE_FUTURE_PRICE", payload: data });
    dispatch({ type: "END_LOADING" });
    openSnackbar("Future price updated successfully");
  } catch (error) {
    console.error("Error updating future price:", error);
    openSnackbar("Failed to update future price: " + error.message);
    throw error;
  }
};

export const deleteFuturePrice = (id, openSnackbar) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING });
    await api.deleteFuturePrice(id);
    dispatch({ type: DELETE_FUTURE_PRICE, payload: id });
    dispatch({ type: END_LOADING });
    openSnackbar("Future price deleted successfully");
  } catch (error) {
    console.error("Error deleting future price:", error);
    openSnackbar("Failed to delete future price: " + error.message);
    throw error;
  }
};

